import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import StatusDot from "@shared/designSystem/StatusDot";
import { SmallText } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { toPercentages } from "@utils/Percentages";

export interface AttendanceComplianceSegment {
    id: string;
    /** Legend text after the percentage, e.g. "a tiempo" */
    label: string;
    count: number;
    color: string;
}

interface AttendanceComplianceBarProps {
    segments: AttendanceComplianceSegment[];
}

/**
 * Stacked bar of the month's check-ins (design 2c): one colored segment per
 * status plus a legend with the percentages. Renders nothing without data.
 */
export const AttendanceComplianceBar: React.FC<AttendanceComplianceBarProps> = ({ segments }) => {
    const percentages = toPercentages(segments.map((segment) => segment.count));
    if (percentages.every((value) => value === 0)) {
        return null;
    }

    const legend = segments.map((segment, index) => `${percentages[index]}% ${segment.label}`);

    return (
        <VerticalContainer width="100%" gap={designSystemStyles.gapSm} margin="0">
            <div
                role="img"
                aria-label={legend.join(", ")}
                style={{ display: "flex", gap: "2px", width: "100%", height: "12px", borderRadius: "999px", overflow: "hidden" }}
            >
                {segments.map((segment, index) =>
                    percentages[index] > 0 ? (
                        <div key={segment.id} style={{ width: `${percentages[index]}%`, background: segment.color }} />
                    ) : null
                )}
            </div>
            <HorizontalContainer width="100%" gap={designSystemStyles.gapMd} flexWrap="wrap">
                {segments.map((segment, index) => (
                    <HorizontalContainer key={segment.id} width="auto" gap={designSystemStyles.gapXsm}>
                        <StatusDot color={segment.color} />
                        <SmallText text={legend[index]} color={designSystemStyles.colorTextSupporting} />
                    </HorizontalContainer>
                ))}
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default AttendanceComplianceBar;
